import { Component, HostBinding, Inject, Input, OnDestroy, OnInit, Optional, ViewEncapsulation } from '@angular/core';
import { LoaderService } from './loader/loader.service';
import { ApdLoaderConfig, LOADER_CONFIG } from './loader/loader.config';
import { Observable, share, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { LanguageState } from '@app/core/store/reducers/language';
import { TranslateService } from '@ngx-translate/core';
import { ChangeLanguage, ClearSearchText } from '@dwo/actions';
import { takeUntil } from 'rxjs/operators';
import { getCurrentUser } from '@dwo/selectors';
import { UserDto } from '@app/generated/model';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  public animationTime = '0.1s 3000ms ease-in-out';

  @Input()
  hideUserLogin = false;

  @Input() offlineMode = false;

  @Input()
  logoClickUrl = 'https://www.akersolutions.com/';

  @HostBinding('class.app-header')
  classAppHeader = true;

  public isLoading: Observable<boolean>;

  public language = 'en';
  public selectedProjectsLabel = '';
  private readonly destroy$ = new Subject<void>();
  private currentUser: UserDto | undefined;

  constructor(
    private loaderService: LoaderService,
    private store: Store<LanguageState>,
    private translateService: TranslateService,
    @Optional() @Inject(LOADER_CONFIG) private loaderConfig: ApdLoaderConfig,
  ) {}

  public ngOnInit(): void {
    this.isLoading = this.loaderService.isLoadingStream.pipe(share());
    if (this.loaderConfig && this.loaderConfig.delayLoadbar != null) {
      this.setDelayForLoadbar(this.loaderConfig.delayLoadbar);
    }

    const language = localStorage.getItem('language');
    if (language) {
      this.language = language;
      this.setLanguage(language);
    }

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserDto) => {
        this.currentUser = user;
        this.selectedProjectsLabel = this.buildSelectedProjectsLabel();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public clearSearch(): void {
    this.store.dispatch(new ClearSearchText());
  }

  private setDelayForLoadbar(delay: number): void {
    this.animationTime = `0.1s ${delay}ms ease-in-out`;
  }

  setLanguage(language: string): void {
    this.language = language;
    this.translateService.use(language);
    this.store.dispatch(new ChangeLanguage(language));
    localStorage.setItem('language', language);
  }

  private buildSelectedProjectsLabel(): string {
    const projectNames = (this.currentUser?.projects || [])
      .map((project) => (project?.name || '').toString().trim())
      .filter((name) => !!name);

    if (projectNames.length === 1) {
      return projectNames[0];
    }

    if (projectNames.length === 2) {
      return `${projectNames[0]}, ${projectNames[1]}`;
    }

    if (projectNames.length > 2) {
      return 'multiple projects';
    }

    return '';
  }
}
